import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const HERO_BG = "/hero-bg.png";

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full overflow-hidden flex flex-col"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/40" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
      <div className="grain absolute inset-0" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-6 md:px-12 pt-32 sm:pt-40 md:pt-48 pb-16 sm:pb-24 flex-1 flex items-end min-h-[100svh]">
        <div className="grid grid-cols-12 gap-6 items-end w-full">
          <div className="col-span-12 lg:col-span-9">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="flex items-start sm:items-center gap-3 sm:gap-4 mb-8 sm:mb-10"
            >
              <span className="hidden sm:block h-px w-10 bg-[#C5A880] flex-shrink-0 mt-2" />
              <span className="text-[10px] sm:text-[11px] font-semibold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-[#C5A880] leading-relaxed">
                AI-Driven Product Strategist <span className="text-[#C5A880]/60">|</span> Former CPO <span className="text-[#C5A880]/60">|</span> Strategic Advisor to CEOs &amp; Founders
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
              data-testid="hero-headline"
              className="font-serif-display text-[34px] xs:text-[40px] sm:text-5xl md:text-7xl lg:text-[88px] font-light leading-[1] sm:leading-[0.95] tracking-tight text-[#F5F5F0] break-words"
            >
              I help CEOs make the{" "}
              <em className="italic text-[#C5A880] font-light">AI-product bets</em>{" "}
              that move revenue, valuation and market position.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.25 }}
              data-testid="hero-subtext"
              className="mt-8 sm:mt-10 max-w-2xl text-[15px] sm:text-base md:text-lg text-[#A3A39A] leading-relaxed"
            >
              Three decades building global SaaS and ConTech platforms. Today I
              partner with founders and boards to turn AI ambition into product
              leadership — without the hype tax.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
              className="mt-10 sm:mt-12 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-5"
            >
              <a
                href="mailto:hello@denisantony.com"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-4 min-h-[48px] bg-[#C5A880] text-[#050505] text-[12px] font-semibold tracking-[0.25em] uppercase hover:bg-[#D8BE96] transition-all duration-300 w-full sm:w-auto"
              >
                Work With Me
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#engage"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-4 min-h-[48px] border border-[#2a2a2a] text-[#F5F5F0] text-[12px] font-semibold tracking-[0.25em] uppercase hover:border-[#C5A880] hover:text-[#C5A880] transition-colors duration-300 w-full sm:w-auto"
              >
                See How I Engage
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom meta strip — static on mobile to avoid overlap, sticky on desktop */}
      <div className="relative lg:absolute lg:bottom-0 lg:left-0 lg:right-0 z-10 border-t border-[#161616] bg-[#050505]/70 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-5 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-5 text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-[#A3A39A]">
          <div>
            <div className="text-[#C5A880]">30+ Years</div>
            <div>At the top of product</div>
          </div>
          <div>
            <div className="text-[#C5A880]">5 Regions</div>
            <div>UK · EU · US · ME · APAC</div>
          </div>
          <div>
            <div className="text-[#C5A880]">3 Domains</div>
            <div>ConTech · SaaS · AI</div>
          </div>
          <div>
            <div className="text-[#C5A880]">CEO · Board</div>
            <div>Trusted advisor</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
